<template>
  <section>
    <one-line-form :submit="updatePassword" :submittable="isValid" :saving="isSaving ? hiddenPassword : ''">
      <transition name="fade">
        <div class="password">
          <input type="email" autocomplete="email" :value="credentials.email" hidden>
          <input-field :title="i18n.t('password:password')" name="password" :type="getInputType" :value="password" class="unmaskPassword" :placeholder="i18n.t('password:placeholder')" @input="syncPassword" :autocorrect="false" :maxlength="partner.passwordRules.maxLength.toString()" :cleanRegExp="new RegExp(/\s+$/)" :pattern="isJapaneseFlow ? null : pattern" id="passwordField" :hiddenValue="app.hidePassword"/>
          <div class="button-wrapper" @click="toggle" :class="{disappear: isValid && !app.deviceIsDesktop}">
            <button type="button" class="hideButton" :class="{hiddenPass : app.hidePassword}" :title="hidePassTitle"></button>
          </div>
        </div>
      </transition>

      <transition name="fade">
        <helper>
          <p v-show="!isValid">{{ isStrictPasswordEnabled ? strictHelperMessage : helperMessage }}</p>
          <a href="#" @click.prevent="toggle" v-show="isValid && !app.deviceIsDesktop">{{ i18n.t(`password:${app.hidePassword ? 'show' : 'hide'}`) }}</a>
        </helper>
      </transition>
    </one-line-form>

    <show-keyboard :focus-element="input"></show-keyboard>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  .password {
    margin-right: 50px;
  }

  .hideButton {
    position: absolute;
    top: 20px;
    right: 10px;
    z-index: 300;
    font-size: .75rem;
    line-height:42px;
    color: #0082FF;
    background: #fff;
    padding: 0 6px;
    width: 60px;
    height: 50px;
    line-height: 50px;
    box-shadow: none;
    background-image: url('~static/hide.svg');
    background-repeat: no-repeat;
    background-position: center;
    &.hiddenPass{
        background-image: url('~static/show.svg');
        background-position: center;
      }
  }

  .button-wrapper {
    position: absolute;
    z-index: 500;
    top: -20px;
    right: -30px;
    height: 90px;
    width: 80px;
  }

  .disappear {
    display: none;
  }

  .helper {
    white-space: pre-line;
  }
</style>

<script>
  import Vue from 'vue'
  import { mapState, mapGetters } from 'vuex'

  import { setSelection } from '../utils/selection'
  import Breakpoints from '../mixins/breakpoints'
  import AdditionalKeyboard from '../components/AdditionalKeyboard'
import { toASCII } from '../utils/dataProcessing'

  export default Vue.component('credentials-password-screen', {
    components: {
      'additional-keyboard': AdditionalKeyboard
    },
    props: ['screenParams'],
    mixins: [
      Breakpoints
    ],
    data () {
      const credentials = this.$store.state.credentials
      return {
        password: credentials.password,
        isSaving: false,
        isReturningSession: credentials.password !== '' && this.$store.state.app.exited,
        readOnly: true
      }
    },
    mounted () {
      const hide = this.partner.hidePasswordByDefault
      const desktop = this.app.deviceIsDesktop

      if (hide.length) {
        this.$store.dispatch('updateApp', {
          hidePassword: (desktop && hide.indexOf('desktop') >= 0) || (!desktop && hide.indexOf('mobile') >= 0)
        })
      }

      this.focus(this.app.hidePassword)
    },
    computed: {
      ...mapState(['credentials', 'app', 'user', 'emailValidation', 'partner']),
      ...mapGetters(['isJapaneseFlow']),
      isStrictPasswordEnabled () {
        return this.$router.resolve({name: 'password-warning'}).resolved.matched.length
      },

      pattern () {
        return '_'.repeat(this.partner.passwordRules.maxLength)
      },
      hiddenPassword () {
        return this.password.replace(/./g, '•')
      },
      hidePassTitle () {
        if (this.app.hidePassword) {
          return this.i18n.t('password:show')
        }
        return this.i18n.t('password:hide')
      },
      isValid () {
        return this.lettersTest && this.lowercaseLetterTest && this.uppercaseLetterTest && this.digitsTest && this.specialsTest && this.digitsOrSpecials && this.password.trim().length >= this.partner.passwordRules.minLength
      },
      lettersTest () {
        let passwordRegex
        if (this.isJapaneseFlow) {
          passwordRegex = this.partner.passwordRules.japaneseRegexPattern.password
        } else {
          passwordRegex = /[a-zA-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞßàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ]/
        }
        return this.partner.passwordRules.letters ? passwordRegex.test(this.password) && this.lowercaseLetterTest && this.uppercaseLetterTest : true
      },
      uppercaseLetterTest () {
        return this.partner.passwordRules.letterCasing ? /[A-ZÀÁÂÃÄÅÆÇÈÉÊËÌÍÎÏÐÑÒÓÔÕÖØÙÚÛÜÝÞß]/.test(this.password) : true
      },
      lowercaseLetterTest () {
        return this.partner.passwordRules.letterCasing ? /[a-zàáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ]/.test(this.password) : true
      },
      digitsTest () {
        let digitsRegex = this.isJapaneseFlow ? /([\uff10-\uff19]|[\d])/ : /[\d]/
        return this.partner.passwordRules.digits ? digitsRegex.test(this.password) : true
      },
      specialsTest () {
        return this.partner.passwordRules.specials ? /[ \\!\x22#\\$%&\x27\\(\\)\\*\\+,\-\\.\\/\\:;\\<\\=\\>\\?@\\[\\\]\\^_`\\{\\|\\}~]/.test(this.password) : true
      },
      digitsOrSpecials () {
        const password = this.isJapaneseFlow ? toASCII(this.password) : this.password
        return this.partner.passwordRules.digitsOrSpecials ? /[\d]/.test(password) || /[ \\!\x22#\\$%&\x27\\(\\)\\*\\+,\-\\.\\/\\:;\\<\\=\\>\\?@\\[\\\]\\^_`\\{\\|\\}~]/.test(password) : true
      },
      helperMessage () {
        if (this.i18n.exists('password:helper_fixed') && this.i18n.t('password:helper_fixed') !== '' && !this.isValid) {
          return this.i18n.t('password:helper_fixed')
        }

        if (!!this.password && !this.isValid) {
          if (this.password.trim().length < this.partner.passwordRules.minLength) {
            if (!this.digitsTest && !this.lettersTest) {
              if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_digits_casing_letters', { rules: this.partner.passwordRules })
              }
              if (!this.lowercaseLetterTest && this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_digits_lowercase_letters', { rules: this.partner.passwordRules })
              }
              if (this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_digits_uppercase_letters', { rules: this.partner.passwordRules })
              }
              return this.i18n.t('password:helper_digits_letters', { rules: this.partner.passwordRules })
            }

            if (!this.digitsTest && !this.specialsTest) {
              return this.i18n.t('password:helper_digits_specials', { rules: this.partner.passwordRules })
            }

            if (!this.lettersTest && !this.specialsTest) {
              if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_specials_casing_letters', { rules: this.partner.passwordRules })
              }
              if (!this.lowercaseLetterTest && this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_specials_lowercase_letters', { rules: this.partner.passwordRules })
              }
              if (this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_specials_uppercase_letters', { rules: this.partner.passwordRules })
              }
              return this.i18n.t('password:helper_letters_specials', { rules: this.partner.passwordRules })
            }

            if (!this.digitsOrSpecials) {
              return this.i18n.t('password:helper_digits_or_specials', { rules: this.partner.passwordRules })
            }

            if (!this.lettersTest) {
              if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_casing_letters', { rules: this.partner.passwordRules })
              }
              if (!this.lowercaseLetterTest && this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_lowercase_letters', { rules: this.partner.passwordRules })
              }
              if (this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_uppercase_letters', { rules: this.partner.passwordRules })
              }
              return this.i18n.t('password:helper_letters', { rules: this.partner.passwordRules })
            }

            if (!this.digitsTest) {
              return this.i18n.t('password:helper_digits', { rules: this.partner.passwordRules })
            }

            if (!this.specialsTest) {
              return this.i18n.t('password:helper_specials', { rules: this.partner.passwordRules })
            }

            return this.i18n.t('password:helper_short', { rules: this.partner.passwordRules })
          } else {
            if (!this.lettersTest && !this.digitsTest) {
              if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_casing_letter_one_digit', { rules: this.partner.passwordRules })
              }
              if (!this.lowercaseLetterTest && this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_lowercase_letter_one_digit', { rules: this.partner.passwordRules })
              }
              if (this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_uppercase_letter_one_digit', { rules: this.partner.passwordRules })
              }
              return this.i18n.t('password:helper_one_letter_one_digit')
            }

            if (!this.lettersTest && !this.specialsTest) {
              if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_casing_letter_one_special', { rules: this.partner.passwordRules })
              }
              if (!this.lowercaseLetterTest && this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_lowercase_letter_one_special', { rules: this.partner.passwordRules })
              }
              if (this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_uppercase_letter_one_special', { rules: this.partner.passwordRules })
              }
              return this.i18n.t('password:helper_one_letter_one_special')
            }

            if (!this.digitsTest && !this.specialsTest) {
              return this.i18n.t('password:helper_one_digit_one_special')
            }

            if (!this.digitsOrSpecials) {
              return this.i18n.t('password:helper_one_digit_or_one_special')
            }

            if (!this.lettersTest) {
              if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_casing_letter', { rules: this.partner.passwordRules })
              }
              if (!this.lowercaseLetterTest && this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_lowercase_letter', { rules: this.partner.passwordRules })
              }
              if (this.lowercaseLetterTest && !this.uppercaseLetterTest) {
                return this.i18n.t('password:helper_one_uppercase_letter', { rules: this.partner.passwordRules })
              }
              return this.i18n.t('password:helper_one_letter')
            }

            if (!this.digitsTest) {
              return this.i18n.t('password:helper_one_digit')
            }

            if (!this.specialsTest) {
              return this.i18n.t('password:helper_one_special')
            }
          }
        }

        if (!this.isValid) {
          if (this.partner.passwordRules.digits && this.partner.passwordRules.letters && this.partner.passwordRules.specials) {
            if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
              return this.i18n.t('password:helper_empty_all_casing', { rules: this.partner.passwordRules })
            }
            return this.i18n.t('password:helper_empty_all', { rules: this.partner.passwordRules })
          }

          if (this.partner.passwordRules.letters && this.partner.passwordRules.digitsOrSpecials) {
            return this.i18n.t('password:helper_empty_all_digits_or_specials', { rules: this.partner.passwordRules })
          }

          if (this.partner.passwordRules.digits && this.partner.passwordRules.letters) {
            if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
              return this.i18n.t('password:helper_empty_casing_letters_digits', { rules: this.partner.passwordRules })
            }

            return this.i18n.t('password:helper_empty_letters_digits', { rules: this.partner.passwordRules })
          }

          if (this.partner.passwordRules.digits && this.partner.passwordRules.specials) {
            if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
              return this.i18n.t('password:helper_empty_casing_letters_specials', { rules: this.partner.passwordRules })
            }

            return this.i18n.t('password:helper_empty_letters_specials', { rules: this.partner.passwordRules })
          }

          if (this.partner.passwordRules.letters && this.partner.passwordRules.specials) {
            return this.i18n.t('password:helper_empty_digits_specials', { rules: this.partner.passwordRules })
          }

          if (this.partner.passwordRules.letters) {
            if (!this.lowercaseLetterTest && !this.uppercaseLetterTest) {
              return this.i18n.t('password:helper_empty_casing_letters', { rules: this.partner.passwordRules })
            }

            return this.i18n.t('password:helper_empty_letters', { rules: this.partner.passwordRules })
          }

          if (this.partner.passwordRules.digitss) {
            return this.i18n.t('password:helper_empty_digits', { rules: this.partner.passwordRules })
          }

          if (this.partner.passwordRules.specials) {
            return this.i18n.t('password:helper_empty_specials', { rules: this.partner.passwordRules })
          }

          return this.i18n.t('password:helper_short', { rules: this.partner.passwordRules })
        }
      },
      strictHelperMessage () {
        const properties = this.restrictedProperties.map(property => property.toLowerCase())
        return this.helperMessage + '. ' + this.i18n.t('password:helper_donts', {properties: properties.join(', ')})
      },

      restrictedProperties () {
        return this.$store.getters.passwordRestrictedProperties
      },
      getInputType () {
        return ['JP'].includes(this.app.country.code) && this.app.hidePassword ? 'password' : 'text'
      }
    },
    methods: {
      input () {
        if (!this.$el) return {}
        return this.$el.querySelector('#passwordField input')
      },
      resetPassword () {
        this.isReturningSession = false
        this.password = ''
      },

      syncPassword (value) {
        if (this.password !== value && this.isReturningSession) this.resetPassword()
        else this.password = value
      },

      updatePassword () {
        if (this.password) {
          this.isSaving = true
          this.readOnly = true
          this.$store.dispatch('updateCredentials', {
            password: this.password
          })

          this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }
      },

      toggle () {
        if (this.app.hidePassword && this.isReturningSession) this.resetPassword()
        this.$store.dispatch('updateApp', {
          hidePassword: !this.app.hidePassword
        })
        this.$nextTick(() => this.focus(this.app.hidePassword))
      },

      focus (hidePassword) {
        let input = this.$el.querySelector('#passwordField input')
        const length = this.password.length

        if (input) {
          this.readOnly = false
          this.$nextTick(() => setSelection(input, { start: length, end: length }))
        }
      }
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/CredentialsPassword.vue